// The patient page's i18n layer — deliberately LOCAL machinery, 2026-06.
//
// No i18n package. The patient page is the one patient-facing surface and
// its self-containment is a stated security property (local design tokens,
// local assets, no external request, no shared staff-bundle dependency);
// a provider + namespaces + a supply-chain dependency bought for one page
// with two locales is machinery without a mission. What this file gives
// instead is a lookup, an interpolator, a reason-code map and four Intl
// formatters — all of which the pin suite can read mechanically.
//
// Dates, times and distances are NEVER hand-assembled: every one goes
// through Intl with the ACTIVE locale tag, so "Friday, September 25" and
// "viernes, 25 de septiembre" come from the same call.

import en from "./patientScheduling.en";
import es from "./patientScheduling.es";

export const LOCALES = { en, es };
export const DEFAULT_LANG = "en";
export const SUPPORTED_LANGS = ["en", "es"];
export const LOCALE_TAGS = { en: "en-US", es: "es-US" };

// The server's machine-readable `reason` codes → locale keys. The API keeps
// its EN prose (logs, non-page consumers, and the no-oracle property of a
// byte-identical failure body); the patient-visible words are page-side.
// The mapping is TOTAL over the codes the three public routes can emit, and
// pinned as such — a new server code fails the pin instead of showing a
// patient a raw code.
export const REASON_KEYS = {
  link_unavailable: "backendMessages.linkUnavailable",
  rate_limited: "backendMessages.rateLimited",
  // Wave 3 (2026-10-06): the self-booking routes. "taken" has its own words;
  // every other booking failure gets the same plain retry-or-call message.
  taken: "book.taken",
  not_available: "book.failed",
  not_booked: "book.failed",
  unavailable: "book.failed",
};
export const UNKNOWN_REASON_KEY = "errors.unknown";

export function langOrDefault(lang) {
  return SUPPORTED_LANGS.includes(lang) ? lang : DEFAULT_LANG;
}

export function localeTag(lang) {
  return LOCALE_TAGS[langOrDefault(lang)];
}

export function t(lang, key, vars) {
  const dict = LOCALES[langOrDefault(lang)];
  const value = String(key).split(".").reduce(
    (node, part) => (node == null ? undefined : node[part]), dict);
  // A missing key renders the key itself — loud, and unreachable in
  // practice because key parity is pinned.
  if (typeof value !== "string") return String(key);
  return value.replace(/\{(\w+)\}/g, (whole, name) =>
    (vars && name in vars ? String(vars[name]) : whole));
}

export function messageForReason(lang, reason, vars) {
  return t(lang, REASON_KEYS[reason] || UNKNOWN_REASON_KEY, vars);
}

// ── Intl formatting (locale-aware, never hand-assembled) ────────────────
const dtf = (lang, opts) => new Intl.DateTimeFormat(localeTag(lang), opts);

export const fmtWeekday = (lang, date) => dtf(lang, { weekday: "long" }).format(date);
export const fmtMonthDay = (lang, date) =>
  dtf(lang, { month: "long", day: "numeric" }).format(date);
export const fmtFullDate = (lang, date) =>
  dtf(lang, { weekday: "long", month: "long", day: "numeric" }).format(date);

export function fmtClock(lang, hhmm) {
  const [h, m] = String(hhmm).split(":").map(Number);
  const d = new Date(2000, 0, 1, h || 0, m || 0);
  return dtf(lang, { hour: "numeric", minute: "2-digit" }).format(d);
}

export const fmtClockRange = (lang, start, end) =>
  `${fmtClock(lang, start)} \u2013 ${fmtClock(lang, end)}`;
